import { loadIndexes } from '@data/indexes';
import { formatModCaracEntry } from '@domain/peuple';
import DOMPurify from 'dompurify';
import { html } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { marked } from 'marked';

export async function renderPeupleDetailPage(id: string): Promise<unknown> {
  const idx = await loadIndexes();
  const peuple = idx.peupleById.get(id);
  if (!peuple) {
    return html`<h1 class="page-title">Peuple introuvable</h1>
      <p>L'identifiant "${id}" ne correspond a aucun peuple.</p>`;
  }
  const voiePrincipale = peuple.voie_id ? idx.voiePeupleById.get(peuple.voie_id) : null;
  const voiesAlternatives = (peuple.voies_alternatives ?? [])
    .map((vid) => idx.voiePeupleById.get(vid))
    .filter((v): v is NonNullable<typeof v> => !!v);

  const narratifHtml = peuple.narratif
    ? DOMPurify.sanitize(await marked.parse(peuple.narratif))
    : null;

  const bonus = peuple.modificateurs_carac.bonus ?? [];
  const malus = peuple.modificateurs_carac.malus ?? [];

  return html`
    <article class="profil-detail">
      <!-- Header -->
      <header class="profil-header">
        <div class="profil-header-top">
          <span class="profil-famille-badge">Peuple</span>
        </div>
        <h1 class="profil-name" data-morph data-morph-duration="1200">${peuple.nom}</h1>
        <div class="peuple-modifiers">
          ${bonus.map(
            (b) => html`<span class="peuple-mod peuple-mod-bonus">${formatModCaracEntry(b)}</span>`,
          )}
          ${malus.map(
            (m) => html`<span class="peuple-mod peuple-mod-malus">${formatModCaracEntry(m)}</span>`,
          )}
        </div>
      </header>

      <!-- Narratif -->
      ${
        narratifHtml
          ? html`
        <section class="profil-section profil-narratif">
          <div class="profil-prose">${unsafeHTML(narratifHtml)}</div>
        </section>
      `
          : null
      }

      <!-- Voie principale -->
      ${
        voiePrincipale
          ? html`
        <section class="profil-section">
          <h2 class="profil-section-title">Voie de peuple <span class="profil-voie-count">${voiePrincipale.rangs.length} rangs</span></h2>
          <div class="profil-voies">
            <div class="profil-voie-block">
              <cof2-accordion label=${voiePrincipale.nom} open>
                <div class="profil-rangs">
                  ${voiePrincipale.rangs.map(
                    (r) => html`
                    <div class="profil-rang">
                      <div class="profil-rang-header">
                        <span class="profil-rang-num">${r.rang}</span>
                        <span class="profil-rang-name">${r.nom}</span>
                        ${r.type ? html`<span class="profil-rang-type">${r.type}</span>` : null}
                      </div>
                      <p class="profil-rang-desc">${r.description}</p>
                    </div>
                  `,
                  )}
                </div>
              </cof2-accordion>
            </div>
          </div>
        </section>
      `
          : html`
        <section class="profil-section">
          <h2 class="profil-section-title">Voie de peuple</h2>
          <p class="profil-mage-note">
            Pas de voie de peuple dediee. Choix possible parmi les voies alternatives ci-dessous.
          </p>
        </section>
      `
      }

      <!-- Voies alternatives -->
      ${
        voiesAlternatives.length > 0
          ? html`
        <section class="profil-section">
          <h2 class="profil-section-title">Voies alternatives <span class="profil-voie-count">${voiesAlternatives.length}</span></h2>
          <div class="profil-voies">
            ${voiesAlternatives.map(
              (v, i) => html`
              <div class="profil-voie-block" style="animation-delay: ${i * 50}ms">
                <cof2-accordion label=${v.nom}>
                  <div class="profil-rangs">
                    ${v.rangs.map(
                      (r) => html`
                      <div class="profil-rang">
                        <div class="profil-rang-header">
                          <span class="profil-rang-num">${r.rang}</span>
                          <span class="profil-rang-name">${r.nom}</span>
                          ${r.type ? html`<span class="profil-rang-type">${r.type}</span>` : null}
                        </div>
                        <p class="profil-rang-desc">${r.description}</p>
                      </div>
                    `,
                    )}
                  </div>
                </cof2-accordion>
              </div>
            `,
            )}
          </div>
        </section>
      `
          : null
      }
    </article>
  `;
}
